import type { SVGProps } from 'react';

/**
 * The sidebar's own glyph set, traced from the Home Shell design (each glyph
 * is normalised onto one 24-unit grid with its own transform so optical
 * weight matches across the set). All draw in `currentColor` at `1em`, so a
 * row's `font-size` and `color` are the only knobs.
 */
export type SidebarIconProps = SVGProps<SVGSVGElement>;

const base = (props: SidebarIconProps) => ({
  viewBox: '0 0 24 24',
  width: '1em',
  height: '1em',
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
  ...props,
});

export const HomeIcon = (props: SidebarIconProps) => (
  <svg {...base(props)}>
    <g transform="translate(-1.5714 -2.0238) scale(1.1310)" strokeWidth="1.5032"><path d="M3.6 10.7 12 4.1l8.4 6.6"/><path d="M5.8 9.8v9a1.9 1.9 0 0 0 1.9 1.9h8.6a1.9 1.9 0 0 0 1.9-1.9v-9"/></g>
  </svg>
);

export const TasksIcon = (props: SidebarIconProps) => (
  <svg {...base(props)}>
    <g transform="translate(-1.1190 -1.4018) scale(1.1310)" strokeWidth="1.5032"><path d="M9 6h11M9 12h11M9 18h11"/><path d="M3.5 5.5l1 1 1.6-1.8"/><circle cx="4.2" cy="12" r="1"/><circle cx="4.2" cy="18" r="1"/></g>
  </svg>
);

export const GlossaryIcon = (props: SidebarIconProps) => (
  <svg {...base(props)}>
    <g transform="translate(-1.7349 -2.1928) scale(1.1446)" strokeWidth="1.4853"><path d="M12 7.5v11.7"/><path d="M12 7.5C10.2 6.2 7.8 5.6 5 5.7A1.3 1.3 0 0 0 3.7 7v9a1.3 1.3 0 0 0 1.3 1.3c2.8-.1 5.2.5 7 1.9"/><path d="M12 7.5c1.8-1.3 4.2-1.9 7-1.8A1.3 1.3 0 0 1 20.3 7v9a1.3 1.3 0 0 1-1.3 1.3c-2.8-.1-5.2.5-7 1.9"/></g>
  </svg>
);

export const RoutinesIcon = (props: SidebarIconProps) => (
  <svg {...base(props)}>
    <g transform="translate(-2.8052 -4.3474) scale(1.2338)" strokeWidth="1.3779"><circle cx="12" cy="13.6" r="6.9"/><path d="M12 10.3v3.3l2.2 1.5"/><path d="M6.2 6 4.3 7.7"/><path d="M17.8 6 19.7 7.7"/></g>
  </svg>
);

export const SkillsIcon = (props: SidebarIconProps) => (
  <svg {...base(props)}>
    <g transform="translate(-1.9408 -1.3225) scale(1.1243)" strokeWidth="1.5121"><path d="M13.9 3.4 5.6 13.9h5.3l-.5 6.9 8.3-10.4h-5.3z"/></g>
  </svg>
);

export const DatabaseIcon = (props: SidebarIconProps) => (
  <svg {...base(props)}>
    <g transform="translate(-2.4304 -1.9494) scale(1.2025)" strokeWidth="1.4137"><ellipse cx="12" cy="6.5" rx="7.5" ry="2.8"/><path d="M4.5 6.5v5.1c0 1.55 3.36 2.8 7.5 2.8s7.5-1.25 7.5-2.8V6.5"/><path d="M4.5 11.6v5.1c0 1.55 3.36 2.8 7.5 2.8s7.5-1.25 7.5-2.8v-5.1"/></g>
  </svg>
);

export const ReportsIcon = (props: SidebarIconProps) => (
  <svg {...base(props)}>
    <g transform="translate(-2.5506 -2.5506) scale(1.2025)" strokeWidth="1.4137"><path d="M4.2 4.2v14.4a1.4 1.4 0 0 0 1.4 1.4h14.2"/><path d="M7.8 15.7 11.3 11.5 14.5 13.9 18.6 8.6"/></g>
  </svg>
);

export const WorkforceIcon = (props: SidebarIconProps) => (
  <svg {...base(props)} strokeWidth="1.7">
    <path d="M5.9 6.3A3.8 3.8 0 0 1 9.7 2.5H18.1A3.8 3.8 0 0 1 21.9 6.3V18.5"/><rect x="2.5" y="5.9" width="16" height="16" rx="3.8"/><circle cx="8.5" cy="13.5" r="1.03" fill="currentColor" stroke="none"/><circle cx="12.5" cy="13.5" r="1.03" fill="currentColor" stroke="none"/>
  </svg>
);

export const WorkspaceTabIcon = (props: SidebarIconProps) => (
  <svg {...base(props)}>
    <g transform="translate(0.0000 0.0000) scale(1.0000)" strokeWidth="1.7000"><rect x="2.5" y="2.5" width="19" height="19" rx="4.4"/><path d="M12 2.5V21.5"/><path d="M2.5 12H21.5"/></g>
  </svg>
);

export const ChatTabIcon = (props: SidebarIconProps) => (
  <svg {...base(props)}>
    <g transform="translate(-0.1277 -0.2287) scale(1.0106)" strokeWidth="1.6821"><path d="M7 3.4H17A4.4 4.4 0 0 1 21.4 7.8V12.2A4.4 4.4 0 0 1 17 16.6H12.2L7.4 20.8V16.6H7A4.4 4.4 0 0 1 2.6 12.2V7.8A4.4 4.4 0 0 1 7 3.4Z"/></g>
  </svg>
);

export const RailCollapseIcon = (props: SidebarIconProps) => (
  <svg {...base(props)}>
    <g transform="translate(-1.4118 -1.4118) scale(1.1176)" strokeWidth="1.5211"><rect x="3.5" y="3.5" width="17" height="17" rx="3.4"/><path d="M9.2 3.5V20.5"/><path d="M16.45 8.8 13.25 12 16.45 15.2"/></g>
  </svg>
);

export const RailExpandIcon = (props: SidebarIconProps) => (
  <svg {...base(props)}>
    <g transform="translate(-1.4118 -1.4118) scale(1.1176)" strokeWidth="1.5211"><rect x="3.5" y="3.5" width="17" height="17" rx="3.4"/><path d="M9.2 3.5V20.5"/><path d="M13.25 8.8 16.45 12 13.25 15.2"/></g>
  </svg>
);

export const SettingsIcon = (props: SidebarIconProps) => (
  <svg {...base(props)}>
    <g transform="translate(0.1250 0.1250) scale(0.9896)" strokeWidth="1.7179"><path d="M9.84 2.65A9.60 9.60 0 0 1 14.16 2.65L14.26 5.80A6.60 6.60 0 0 1 16.24 6.94L19.02 5.45A9.60 9.60 0 0 1 21.18 9.19L18.50 10.85A6.60 6.60 0 0 1 18.50 13.15L21.18 14.81A9.60 9.60 0 0 1 19.02 18.55L16.24 17.06A6.60 6.60 0 0 1 14.26 18.20L14.16 21.35A9.60 9.60 0 0 1 9.84 21.35L9.74 18.20A6.60 6.60 0 0 1 7.76 17.06L4.98 18.55A9.60 9.60 0 0 1 2.82 14.81L5.50 13.15A6.60 6.60 0 0 1 5.50 10.85L2.82 9.19A9.60 9.60 0 0 1 4.98 5.45L7.76 6.94A6.60 6.60 0 0 1 9.74 5.80L9.84 2.65Z"/><circle cx="12" cy="12" r="2.7"/></g>
  </svg>
);

export const ProfileIcon = (props: SidebarIconProps) => (
  <svg {...base(props)}>
    <g transform="translate(-1.9877 -2.2791) scale(1.1656)" strokeWidth="1.4584"><circle cx="12" cy="8.2" r="4.1"/><path d="M4.6 20.4a7.9 7.9 0 0 1 14.8 0"/></g>
  </svg>
);

export const AppsIcon = (props: SidebarIconProps) => (
  <svg {...base(props)}>
    <g transform="translate(-2.5520 0.1936) scale(1.0983)" strokeWidth="1.5479"><path d="M4.6 7.2A2.6 2.6 0 0 1 7.2 4.6H9.5A2.5 2.5 0 0 1 14.5 4.6H16.8A2.6 2.6 0 0 1 19.4 7.2V9.5A2.5 2.5 0 0 1 19.4 14.5V16.8A2.6 2.6 0 0 1 16.8 19.4H7.2A2.6 2.6 0 0 1 4.6 16.8Z"/></g>
  </svg>
);

export const UpgradeIcon = (props: SidebarIconProps) => (
  <svg {...base(props)}>
    <g transform="translate(-1.4118 -1.4118) scale(1.1176)" strokeWidth="1.5211"><path d="M12 3.5C12.8 8.6 15.4 11.2 20.5 12 15.4 12.8 12.8 15.4 12 20.5 11.2 15.4 8.6 12.8 3.5 12 8.6 11.2 11.2 8.6 12 3.5Z"/></g>
  </svg>
);

export const LogoutIcon = (props: SidebarIconProps) => (
  <svg {...base(props)}>
    <g transform="translate(-3.2000 -3.2000) scale(1.2667)" strokeWidth="1.3421"><path d="M13.2 4.5H7.4A2.9 2.9 0 0 0 4.5 7.4V16.6A2.9 2.9 0 0 0 7.4 19.5H13.2"/><path d="M10.4 12H19.5"/><path d="M16.1 8.6 19.5 12 16.1 15.4"/></g>
  </svg>
);
